import { Component, computed, effect, inject, ChangeDetectionStrategy } from '@angular/core';
import { FolderRiskStatusObject } from '@libs/types/src/services/object-type/folder/folder-risk-status.object';
import { FilterBuilder } from '../../../helper/filter.builder';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfirmBuilder } from '../../../../shared/service/confirm.builder';
import { PersonRisksStore } from './person-risks.store';
import { PersonRisksDialogComponent } from './person-risks-dialog/person-risks-dialog.component';
import { TableColumnType } from '../../../../shared/components/kit/table/table.types';
import { dateField } from '../../../../shared/components/kit/table/table.fields';
import { FolderRiskStatusEnum } from '@libs/types/src/services/enum/folder-risk-status.enum';
import { BaseDialogService } from '../../../../../components/kit/base-dialog/base-dialog-service';
import { DatePipe, NgClass } from '@angular/common';
import { TableComponent } from '../../../../shared/components/kit/table/table.component';
import { PrimeTemplate } from '@openng/optimus-ui/api';

@Component({
    selector: 'app-person-risks',
    templateUrl: './person-risks.component.html',
    providers: [PersonRisksStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
    NgClass,
    TranslatePipe,
    DatePipe,
    TableComponent,
    PrimeTemplate
],
})
export class PersonRisksComponent {
    protected readonly dialogService = inject(BaseDialogService);
    protected readonly store = inject(PersonRisksStore);
    private readonly confirm = inject(ConfirmBuilder);
    private readonly translate = inject(TranslateService);


    protected columns = [
        { name: 'status', type: TableColumnType.String },
        dateField('dateFrom', false, false),
        { name: 'riskType', type: TableColumnType.String },
        { name: 'note', type: TableColumnType.String50 },
    ];

    protected lastDetail = true;

    lastStatus = computed(() => this.store.items()[0]);

    severity = computed(() => {
        const lastStatus = this.lastStatus();
        if (!lastStatus) return '';
        switch (lastStatus.status) {
            case FolderRiskStatusEnum.NO_RISK:
                return 'success';
            case FolderRiskStatusEnum.RISKY:
                return 'warning';
            case FolderRiskStatusEnum.POSITIVE:
                return 'danger';
        }
        return '';
    });

    constructor() {
        const store = this.store;
        store.doOnActions(
            () => this.getRiskStatuses(),
            'updateFolderRiskStatus', 'createFolderRiskStatus', 'deleteFolderRiskStatus',
        );

        effect(() => {
            this.getRiskStatuses();
        });
    }

    switchEdit() {
        this.lastDetail = !this.lastDetail;
    }

    editLastStatus() {
        this.openDialog(this.lastStatus());
    }

    openDialog(risk?: FolderRiskStatusObject | null) {
        this.dialogService.open(PersonRisksDialogComponent, {
            data: { risk },
            header: this.translate.instant(risk ? 'person.dashboard.riskStatus.edit' : 'person.dashboard.riskStatus.create'),
        });
    }

    protected detail(item: FolderRiskStatusObject) {
        this.openDialog(item);
    }

    protected delete(event: any) {
        this.confirm.confirmDelete(
            () => this.store.api.deleteFolderRiskStatus({ id: event.item.id }),
            event,
        );
    }

    private async getRiskStatuses(): Promise<void> {
        const filter = new FilterBuilder()
            .where('folderId', this.store.folderId(), 'IN')
            .limit(10)
            .sort('dateFrom', 'DESC');
        await this.store.api.getFolderRiskStatuses(filter.create());
    }

}
